/**
 * Hides the footer's Add To Cart while the box is incomplete, and brings it back only if
 * the auto-checkout did not fire.
 *
 * Why it is not simply hidden. The auto-checkout in the Easy Bundles app-global JS box calls
 * the SAME handler this button calls — onAddToCartClick — and it has an explicit give-up
 * path: if that handler is missing it logs "[TB] auto-checkout: no add-to-cart handler" and
 * returns. Someone wrote that branch because they expected it to happen. In that state a
 * shopper has a full box and this button is the only remaining way to pay, so removing it
 * turns a logged, recoverable failure into a silent dead end.
 *
 * So the button goes only where it cannot help: below a full box, where it is useless and —
 * measured — still enabled, tappable at zero picks with no disabled attribute. At a full box
 * it comes back after a beat, which in the ordinary case never arrives because the
 * auto-checkout has already navigated away.
 *
 * FAIL OPEN is the rule here. Any state this cannot read — no footer, no slots, a shape it
 * does not recognise — shows the button. Guessing wrong in that direction costs a redundant
 * control; guessing wrong the other way costs the order.
 */

export const HIDDEN_ATTR = "data-tb-cart";
/**
 * Marks the review panel open or shut, because nothing else does.
 *
 * Expanding it changes no class and no attribute anywhere on the page — the only difference
 * measured between the two states is that the slot boxes go from 0px to 60px tall. CSS
 * cannot select on that, so this publishes it as an attribute for the stylesheet to use.
 */
export const STATE_ATTR = "data-tb-footer";
/**
 * How long a full box waits before the fallback appears. Long enough that the auto-checkout
 * wins the race in the ordinary case and the button is never seen; short enough that a
 * shopper whose auto-checkout failed is not left staring at a complete box.
 */
export const REVEAL_DELAY_MS = 1500;

/**
 * { filled, required } from the footer's slots, or null when it cannot be read.
 * Slots rather than the "N of 5 picked" copy: the copy is content and can be reworded, the
 * slots are structure.
 */
export function boxState(doc = document) {
  const slots = doc.querySelectorAll(
    ".gbbBundleFooter .gbbProductSlotContainer",
  );
  if (!slots.length) return null;
  let filled = 0;
  for (const slot of slots) if (slot.dataset.productId) filled++;
  return { filled, required: slots.length };
}

/**
 * Whether the button should be on screen. Unknown states show it.
 *
 * BOTH counts are checked for being real numbers, not just `required`. A NaN filled count
 * fails `filled >= required` and would resolve to hidden — the one direction that costs an
 * order. Every unreadable state has to land on "show".
 */
export function shouldShow(state) {
  if (!state) return true;
  if (!Number.isFinite(state.required) || state.required < 1) return true;
  if (!Number.isFinite(state.filled)) return true;
  return state.filled >= state.required;
}

/**
 * Whether the review panel is open, read from whether its slots render.
 * Null when there is nothing to read, so the caller can leave the attribute alone rather
 * than assert a state it does not know.
 */
export function isExpanded(doc = document) {
  const slot = doc.querySelector(".gbbBundleFooter .gbbProductSlotContainer");
  if (!slot?.getBoundingClientRect) return null;
  return slot.getBoundingClientRect().height > 0;
}

export function install(doc = document) {
  const footer = () => doc.querySelector(".gbbBundleFooter");
  let timer = null;
  let revealed = false;

  const paint = (show) => {
    const el = footer();
    if (!el) return;
    if (show) el.removeAttribute(HIDDEN_ATTR);
    else el.setAttribute(HIDDEN_ATTR, "hidden");
  };

  const paintState = () => {
    const el = footer();
    if (!el) return;
    const open = isExpanded(doc);
    if (open === null) return; // nothing readable — leave whatever is there
    el.setAttribute(STATE_ATTR, open ? "expanded" : "collapsed");
  };

  const sync = () => {
    paintState();
    const state = boxState(doc);
    const full = shouldShow(state);

    if (!full) {
      // Back below a full box: cancel any pending reveal and hide again.
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      revealed = false;
      paint(false);
      return;
    }

    // Full, or unreadable. Unreadable shows at once — that is the fail-open rule.
    if (state === null) {
      paint(true);
      return;
    }
    if (revealed) {
      paint(true);
      return;
    }
    if (timer) return; // a reveal is already pending; do not restart its clock
    timer = setTimeout(() => {
      timer = null;
      revealed = true;
      paint(true);
    }, REVEAL_DELAY_MS);
    paint(false);
  };

  /*
   * Opening the panel changes no markup at all — only heights — so a MutationObserver on
   * childList never hears about it and the state attribute would sit stale from load. A
   * ResizeObserver on the footer is what actually catches the open and the shut.
   *
   * Re-pointed rather than attached once, because the app replaces the whole footer element
   * on every cart change and an observer left on the old node watches a detached div.
   */
  let watched = null;
  const resize =
    typeof ResizeObserver === "function" ? new ResizeObserver(sync) : null;

  const rewatch = () => {
    const el = footer();
    if (!resize || !el || el === watched) return;
    if (watched) resize.unobserve(watched);
    resize.observe(el);
    watched = el;
  };

  const tick = () => {
    rewatch();
    sync();
  };

  tick();
  // The app replaces the whole footer on every cart change, so this cannot be wired to the
  // element once — it has to re-read after each rebuild.
  new MutationObserver(tick).observe(doc.body, {
    childList: true,
    subtree: true,
  });

  /*
   * A click-driven re-read, because the ResizeObserver above cannot be the only trigger.
   * Opening the panel is a height change with no markup change, so if ResizeObserver does
   * not fire the attribute stays stale and the collapsed-only padding applies open — which
   * is exactly what happened when this shipped with the observer alone.
   *
   * The delays cover the panel's own animation: read once immediately for a snap, then again
   * after it has settled. Cheap enough to be unconditional — a tick is two DOM reads and an
   * attribute write that no-ops when nothing changed.
   */
  doc.addEventListener(
    "click",
    () => {
      for (const delay of [0, 350, 700]) setTimeout(tick, delay);
    },
    true,
  );
}

if (typeof document !== "undefined") {
  const start = () => install();
  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", start, { once: true });
  else start();
}
